/* eslint-disable fp/no-mutating-methods */
import {
  Icon, Table, TableCell, TableHeading
} from '@nike/eds';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNposValue } from '../../../../redux/actions';
import { getBodyTranslations, getNposValue } from '../../../../redux/selectors';
import { translationReplaceNumber } from '../../../../utils/formatting';
import { getColAlignment, sortFn } from '../nposUtils';

import NoResults from './NoResults';
import TableLoading from './TableLoading';

import './npos-table.css';

const selectedStyle = {
  background: '#E5E5E5',
};

const ITEMS_PER_PAGE = 20;
const DEFAULT_COL_WIDTH = 100;

function NposTable({
  columns,
  data,
  handleClick,
  isTransactionSelected,
  fetching,
  fetched,
  errorMessage
}) {
  const [rows, setRows] = useState([]);
  const [sortBy, setSortBy] = useState('transactionStartDate');
  const [sortOrder, setSortOrder] = useState('dsc');
  const currentPage = useSelector((state) => state.nposValues.currentPage);
  const { isHomePage } = useSelector(getNposValue);
  const translations = useSelector(getBodyTranslations);

  const [selectedRow, setSelectedRow] = useState(null);

  const dispatch = useDispatch();
  const setCurrentPage = (page) => dispatch(setNposValue({ currentPage: page }));

  useEffect(() => {
    const sortedRows = data?.length && sortBy
      ? [...data].sort((a, b) => sortFn(
            a,
            b,
            sortBy,
            sortOrder,
            columns.find((col) => col.id === sortBy)?.formatType
          ))
      : data;
    setRows(sortedRows);
  }, [data, sortBy, sortOrder, columns]);

  const scrollToView = () => {
    const element = document.getElementById('receipt-view');
    if (element) element.scrollTo(0, 0);
  };

  const changeSorting = (columnId) => {
    if (columnId === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'dsc' : 'asc');
    } else {
      setSortBy(columnId);
    }
  };

  const handleRowClick = (row, rowIdx) => {
    handleClick(row, rowIdx);
    setSelectedRow(row);
    scrollToView();
  };

  const totalPages = Math.ceil(rows.length / ITEMS_PER_PAGE);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedRows = rows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const renderPageNumbers = () => Array.from({ length: totalPages }, (_, i) => (
    <span key={i + 1} className="page-number-container">
      <button
        className={`page-number-button ${i + 1 === currentPage ? 'active' : ''}`}
        style={{ fontWeight: i + 1 === currentPage ? 'bold' : 'normal', paddingTop: i + 1 === currentPage ? '1.6rem' : '0rem' }}
        type="button"
        onClick={() => handlePageChange(i + 1)}
      >
        {i + 1}
      </button>
      {i + 1 === currentPage && (
        <div className="page-number-icon">
          <Icon name="Minus" size="m" />
        </div>
      )}
    </span>
  ));

  const isPaginated = rows.length > ITEMS_PER_PAGE;

  const getMatchingTransactionsText = (len) => (len > 1 ? translationReplaceNumber(translations.RL_MatchingTransactions2, len) : translations.RL_MatchingTransactions1);

  return fetching ? (
    <TableLoading columns={columns} />
  ) : (
    <div className="npos-table">
      {!!rows.length && fetched && (
        <div className="d-sm-flx transactions-msg" data-testid="search-result-msg">{isHomePage ? translations.RL_LatestTransactions : getMatchingTransactionsText(rows.length)}</div>
      )}
      {rows.length && fetched ? (
        <>
          <Table
            style={{
              backgroundColor: '#fff',
              width: isTransactionSelected ? '100%' : '100%',
            }}
          >
            <thead style={{ width: !isPaginated ? '100%' : '' }}>
              <tr>
                {columns.map((column, idx) => {
                  const isSortBy = sortBy === column.id;

                  return (
                    <TableHeading
                      key={column.id}
                      style={{ minWidth: column.width || DEFAULT_COL_WIDTH, textAlign: 'center' }}
                      onClick={() => changeSorting(column.id)}
                    >
                      <div
                        className={`d-sm-flx flx-dir-r flx-ai-sm-c flx-jc-sm-${getColAlignment(
                          idx,
                          columns.length
                        )} ${
                          isSortBy ? 'column-header-sortby' : 'column-header'
                        }`}
                        style={{ minHeight: '50px' }}
                      >
                        <div
                          className="d-sm-flx flx-dir-r"
                          style={{ gap: '10px' }}
                        >
                          <div>{column.Header}</div>
                          {isSortBy && (
                            <div style={{ visibility: isSortBy ? '' : 'hidden' }}>
                              {sortOrder === 'asc' ? (
                                <Icon name="SortAscending" size="m" />
                              ) : (
                                <Icon name="SortDescending" size="m" />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableHeading>
                  );
                })}
              </tr>
            </thead>
            <tbody className={isPaginated ? 'paginate' : 'no-paginate'}>
              {paginatedRows.map((row, rowIdx) => {
                const actualIndex = rowIdx + (currentPage - 1) * ITEMS_PER_PAGE;

                return (
                  <tr
                    key={actualIndex}
                    data-testid={`npos-table-row-${actualIndex}`}
                    style={row?.rowId === selectedRow?.rowId ? selectedStyle : {}}
                    onClick={() => handleRowClick(row, actualIndex)}
                  >
                    {columns.map(({ id, accessor, width }, idx) => (
                      <TableCell
                        key={id}
                        style={{
                        minWidth: width || DEFAULT_COL_WIDTH
                      }}
                      >
                        <div
                          className={`d-sm-flx flx-dir-r flx-jc-sm-${getColAlignment(
                          idx,
                          columns.length
                        )}`}
                          style={{ flexGrow: 1 }}
                        >
                          <div className="npos-table-cell">
                            {typeof accessor === 'function'
                            ? accessor(row)
                            : row[id]}
                          </div>
                        </div>
                      </TableCell>
                  ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {isPaginated && (
            <div style={{
              alignItems: 'center', display: 'flex', justifyContent: 'center', width: '100%'
              }}
            >
              <div className="pagination-controls">
                <button
                  className="page-number-button"
                  disabled={currentPage === 1}
                  style={{
                    color: currentPage === 1 ? '#D3D3D3' : '#000'
                    }}
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <Icon name="CaretLeft" size="m" />
                </button>
                {renderPageNumbers()}
                <button
                  className="page-number-button"
                  disabled={currentPage === totalPages}
                  style={{
                    color: currentPage === totalPages ? '#D3D3D3' : '#000'
                    }}
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <Icon name="CaretRight" size="m" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <NoResults errorMessage={errorMessage} translations={translations} />
      )}
    </div>
  );
}

NposTable.defaultProps = {
  errorMessage: null,
};

NposTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  errorMessage: PropTypes.string,
  fetched: PropTypes.bool.isRequired,
  fetching: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  isTransactionSelected: PropTypes.bool.isRequired
};

export default NposTable;
